import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useAuthorization } from '@/hooks/useAuthorization';
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  UserIcon,
  TruckIcon,
  MapPinIcon,
  BellIcon,
  BuildingOffice2Icon,
  DocumentArrowDownIcon,
  ArrowRightOnRectangleIcon,
  AdjustmentsHorizontalIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  DocumentTextIcon,
  CloudIcon,
  Squares2X2Icon,
  TagIcon,
  MapIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { MeteoWidget } from '@/components/meteo';

/**
 * Configuration de la navigation principale
 * Chaque item peut avoir une propriete 'roles' pour le filtrage
 */
const navigation = [
  { name: 'Tableau de bord', href: '/', icon: HomeIcon },
  { name: 'Interventions', href: '/deneignements', icon: ClipboardDocumentListIcon },
  {
    name: 'Regulation',
    href: '/regulation',
    icon: AdjustmentsHorizontalIcon,
    roles: ['ROLE_ADMIN', 'ROLE_REGULATEUR', 'ROLE_GID_REGUL'],
  },
  {
    // SFD "Carte du statut hivernal des postes" section 6 : tout
    // utilisateur connecte, y compris ROLE_COMPAGNIE (decision #C00b) -
    // pas de propriete `roles` = visible par tous (cf filtrage plus bas).
    name: 'Carte des postes',
    href: '/carte',
    icon: MapIcon,
  },
  {
    name: 'Exports',
    href: '/exports',
    icon: DocumentArrowDownIcon,
    roles: ['ROLE_ADMIN', 'ROLE_GID_REGUL', 'ROLE_REGULATEUR'],
  },
  {
    name: 'Paramétrage METAR',
    href: '/administration/metar',
    icon: CloudIcon,
    roles: ['ROLE_ADMIN', 'ROLE_GID_REGUL'],
  },
  {
    name: 'Materiel',
    href: '/administration/materiel',
    icon: TruckIcon,
    roles: ['ROLE_ADMIN', 'ROLE_GID_REGUL', 'ROLE_REGULATEUR', 'ROLE_CHEF_DENEIGNEMENT'],
  },
  {
    name: 'Compatibilité',
    href: '/administration/compatibilite',
    icon: Squares2X2Icon,
    roles: ['ROLE_ADMIN', 'ROLE_GID_REGUL', 'ROLE_REGULATEUR'],
  },
];

/**
 * Configuration de la navigation administration
 */
const adminNavigation = [
  { name: 'Utilisateurs', href: '/administration/utilisateurs', icon: UserIcon },
  { name: 'Equipes', href: '/administration/equipes', icon: UserGroupIcon },
  {
    name: 'Planning',
    href: '/administration/planning',
    icon: CalendarDaysIcon,
    roles: ['ROLE_ADMIN', 'ROLE_CHEF_DENEIGNEMENT'],
  },
  { name: 'Parkings', href: '/administration/parkings', icon: MapPinIcon },
  { name: 'Compagnies', href: '/administration/compagnies', icon: BuildingOffice2Icon },
  { name: 'Types de materiel', href: '/administration/types-materiel', icon: TagIcon },
  { name: 'Notifications', href: '/administration/notifications', icon: BellIcon },
  {
    name: 'Rapport imports',
    href: '/administration/imports',
    icon: DocumentTextIcon,
    roles: ['ROLE_ADMIN'],
  },
];

/**
 * Layout principal de l'application
 */
export function Layout() {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { hasAnyRole, canAccess } = useAuthorization();

  /**
   * Filtre les items de navigation selon les roles de l'utilisateur
   */
  const filterNavByRoles = (items: typeof navigation) => {
    return items.filter((item) => {
      if (!item.roles) return true;
      return hasAnyRole(item.roles);
    });
  };

  const visibleNavigation = filterNavByRoles(navigation);
  const visibleAdminNavigation = filterNavByRoles(adminNavigation);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-white shadow-lg">
        {/* Logo */}
        <div className="flex items-center justify-center h-20 bg-white border-b px-4">
          <img
            src="/logo_falcon.png"
            alt="FALCON"
            className="h-12"
          />
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3">
          <ul className="space-y-1">
            {visibleNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={clsx(
                      'flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors',
                      isActive
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    )}
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Section Administration - visible uniquement pour les admins */}
          {canAccess.admin && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Administration
              </p>
              <ul className="space-y-1">
                {visibleAdminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={clsx(
                          'flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                          isActive
                            ? 'bg-primary-50 text-primary-600'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        )}
                      >
                        <item.icon className="w-5 h-5 mr-3" />
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </nav>

        {/* User info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center justify-between">
            <Link
              to="/profile"
              className="flex items-center hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors"
              title="Mon profil"
            >
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 text-sm font-medium">
                  {user?.prenom?.[0]}{user?.nom?.[0]}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{user?.nomComplet}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </Link>
            <div className="flex items-center space-x-1">
              <Link
                to="/profile"
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Mon profil"
              >
                <UserCircleIcon className="w-5 h-5" />
              </Link>
              <button
                onClick={logout}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Deconnexion"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64">
        {/* Header avec Widget Meteo */}
        <header className="bg-white shadow-sm">
          <div className="px-6 py-3 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-800">
              Module Deneigement des Parkings
            </h1>
            {/* Widget Meteo temps reel */}
            <MeteoWidget icao="LFPO" />
          </div>
        </header>

        {/* Page content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default Layout;
